/**
 * Shared date-window math for the Marketing Dashboard.
 *
 * Every tab on /marketing/dashboard answers the same question — "how did
 * this channel do over the last N days, and is that better or worse than
 * the N days before?" — so all four data hooks fetch a *doubled* window
 * and split it in half: the recent half is the current period, the older
 * half is the comparison baseline. That's why `windowStartIso` reaches
 * back `days * 2`.
 *
 * The alternative (two queries per metric, or a DB-side aggregate) costs
 * more roundtrips for the same payload; these daily rollup tables are
 * narrow enough that 180 days of rows is well under a megabyte.
 */

export type MarketingRange = '7d' | '28d' | '90d';

export const MARKETING_RANGE_DAYS: Record<MarketingRange, number> = {
  '7d': 7,
  '28d': 28,
  '90d': 90,
};

export const MARKETING_RANGE_OPTIONS: Array<{
  id: MarketingRange;
  label: string;
  shortLabel: string;
}> = [
  { id: '7d', label: 'Last 7 days', shortLabel: '7d' },
  { id: '28d', label: 'Last 28 days', shortLabel: '28d' },
  { id: '90d', label: 'Last 90 days', shortLabel: '90d' },
];

/** ISO date (YYYY-MM-DD) `daysAgo` days before today, in UTC. */
export function isoDaysAgo(daysAgo: number): string {
  const d = new Date();
  d.setUTCHours(0, 0, 0, 0);
  d.setUTCDate(d.getUTCDate() - daysAgo);
  return d.toISOString().slice(0, 10);
}

/**
 * Lower bound for a query that needs both the current period and its
 * comparison baseline.
 */
export function comparisonWindowStartIso(days: number): string {
  return isoDaysAgo(days * 2);
}

/** Inclusive lower bound of the current (recent) half of the window. */
export function currentWindowStartIso(days: number): string {
  return isoDaysAgo(days);
}

/**
 * Splits day-keyed rows into the current period and the prior baseline.
 *
 * Rows are bucketed by their own `day` value rather than by position in
 * the array, because these rollup tables are sparse: a day with no traffic
 * (or a day inside Search Console's 2-3 day reporting lag) simply has no
 * row. Splitting by index would silently shift the comparison window.
 */
export function splitByPeriod<T extends { day: string }>(
  rows: T[],
  days: number,
): { current: T[]; previous: T[] } {
  const boundary = currentWindowStartIso(days);
  const current: T[] = [];
  const previous: T[] = [];
  for (const row of rows) {
    if (row.day >= boundary) current.push(row);
    else previous.push(row);
  }
  return { current, previous };
}

/**
 * Percentage change from `previous` to `current`, as a 0..1 ratio suitable
 * for `MetricCard`'s `delta` prop.
 *
 * Returns null — rendered as "no comparison" rather than a misleading
 * arrow — when the baseline is 0. A jump from 0 to anything is an infinite
 * percentage increase, and showing "+100%" or "+∞%" in a report the team
 * reviews every two weeks invites bad decisions.
 */
export function pctChange(
  current: number,
  previous: number,
): number | null {
  if (!Number.isFinite(current) || !Number.isFinite(previous)) return null;
  if (previous === 0) return null;
  return (current - previous) / Math.abs(previous);
}

/**
 * Inverted variant for metrics where lower is better (average search
 * position, cost per click, cost per acquisition). Callers pass the raw
 * values and get back a delta whose sign means "good/bad" rather than
 * "up/down", so the shared green-up / red-down MetricCard treatment stays
 * correct without every caller special-casing it.
 */
export function pctChangeInverted(
  current: number,
  previous: number,
): number | null {
  const raw = pctChange(current, previous);
  return raw === null ? null : -raw;
}

/** Sum of a numeric field across rows. */
export function sumBy<T>(rows: T[], pick: (row: T) => number): number {
  let total = 0;
  for (const row of rows) total += pick(row) || 0;
  return total;
}

/**
 * Impressions-weighted mean — the only correct way to roll up Search
 * Console's average position across days or dimensions.
 *
 * A plain average treats a query with 3 impressions at position 2 the same
 * as one with 30,000 impressions at position 40, which produces a headline
 * position that no amount of real-world movement can explain. Returns 0
 * when there's no weight at all so the UI can treat it as "no data".
 */
export function weightedMean<T>(
  rows: T[],
  pickValue: (row: T) => number,
  pickWeight: (row: T) => number,
): number {
  let weighted = 0;
  let weight = 0;
  for (const row of rows) {
    const w = pickWeight(row) || 0;
    if (w <= 0) continue;
    weighted += (pickValue(row) || 0) * w;
    weight += w;
  }
  return weight === 0 ? 0 : weighted / weight;
}

/** Safe ratio that yields 0 instead of NaN/Infinity on a zero denominator. */
export function safeRatio(numerator: number, denominator: number): number {
  if (!denominator) return 0;
  const out = numerator / denominator;
  return Number.isFinite(out) ? out : 0;
}

/**
 * Fills gaps in a sparse daily series so charts render a continuous line
 * instead of interpolating across missing days.
 *
 * `emptyFor` builds a zeroed point for a missing date. Without this, a
 * platform that reports nothing on weekends draws a straight line between
 * Friday and Monday, which reads as sustained activity that never
 * happened.
 */
export function fillDailyGaps<T extends { day: string }>(
  rows: T[],
  days: number,
  emptyFor: (day: string) => T,
): T[] {
  const byDay = new Map(rows.map((r) => [r.day, r]));
  const out: T[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const day = isoDaysAgo(i);
    out.push(byDay.get(day) ?? emptyFor(day));
  }
  return out;
}

/** Short axis label, e.g. "Aug 4". */
export function formatShortDay(value: string): string {
  const d = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/** Tooltip label, e.g. "Mon, Aug 4". */
export function formatTooltipDay(value: string): string {
  const d = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/** Compact integer, e.g. 12.4K. Keeps metric tiles from wrapping. */
const COMPACT = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

export function formatCompactNumber(value: number): string {
  if (!Number.isFinite(value)) return '—';
  return Math.abs(value) >= 10_000
    ? COMPACT.format(value)
    : Math.round(value).toLocaleString();
}

/** Seconds → "4m 12s" / "38s". Used for engagement + watch time. */
export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '0s';
  const total = Math.round(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return minutes > 0 ? `${minutes}m ${secs}s` : `${secs}s`;
}

/** Ratio (0..1) → "3.4%". */
export function formatRatioPercent(value: number, digits = 1): string {
  if (!Number.isFinite(value)) return '—';
  return `${(value * 100).toFixed(digits)}%`;
}

/**
 * Inclusive date window used by the Marketing Dashboard hooks *and* the
 * Sales & Marketing dashboard. `MarketingRange` ('7d' / '28d' / '90d') is
 * converted into one of these so a page with richer presets (today, MTD,
 * yesterday, …) can reuse the same fetch + split without each hook
 * knowing about every preset.
 *
 * Previous and current ranges are independent — they don't have to be
 * adjacent. Year-to-date compares to last year-to-date, which leaves a
 * gap; `splitByWindow` drops rows that fall in that gap rather than
 * folding them into the baseline.
 */
export interface AnalyticsWindow {
  previousStart: string;
  previousEnd: string;
  currentStart: string;
  currentEnd: string;
}

/** Shift an ISO date (YYYY-MM-DD) by `days` in UTC. */
export function addUtcDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Inclusive day count between two ISO dates. */
export function daysInclusive(start: string, end: string): number {
  const a = new Date(`${start}T00:00:00Z`).getTime();
  const b = new Date(`${end}T00:00:00Z`).getTime();
  if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return 0;
  return Math.round((b - a) / 86_400_000) + 1;
}

/** Every ISO date from `start` through `end`, inclusive, in UTC. */
export function eachIsoDay(start: string, end: string): string[] {
  const out: string[] = [];
  const cursor = new Date(`${start}T00:00:00Z`);
  const last = new Date(`${end}T00:00:00Z`);
  if (Number.isNaN(cursor.getTime()) || Number.isNaN(last.getTime())) return out;
  while (cursor <= last) {
    out.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return out;
}

/**
 * Maps the dashboard's three presets onto an `AnalyticsWindow` that
 * preserves the existing off-by-one (a 7-day preset starts `isoDaysAgo(7)`,
 * so the current half is eight calendar dates including today). Callers
 * that want a clean inclusive N-day window should build the window
 * themselves rather than going through this helper.
 */
export function resolveAnalyticsWindow(
  range: MarketingRange | AnalyticsWindow,
): AnalyticsWindow {
  if (typeof range !== 'string') return range;
  const days = MARKETING_RANGE_DAYS[range];
  const currentStart = currentWindowStartIso(days);
  const currentEnd = isoDaysAgo(0);
  return {
    previousStart: comparisonWindowStartIso(days),
    previousEnd: addUtcDays(currentStart, -1),
    currentStart,
    currentEnd,
  };
}

/**
 * Splits day-keyed rows into the current period and its comparison
 * baseline using explicit inclusive bounds. Rows that fall in neither
 * half (the gap between last-year YTD and this-year YTD, for example)
 * are dropped.
 */
export function splitByWindow<T extends { day: string }>(
  rows: T[],
  window: AnalyticsWindow,
): { current: T[]; previous: T[] } {
  const current: T[] = [];
  const previous: T[] = [];
  for (const row of rows) {
    if (row.day >= window.currentStart && row.day <= window.currentEnd) {
      current.push(row);
    } else if (row.day >= window.previousStart && row.day <= window.previousEnd) {
      previous.push(row);
    }
  }
  return { current, previous };
}

/**
 * Fills gaps across an arbitrary inclusive date range so a chart whose
 * window isn't "the last N days from today" (yesterday, MTD, a clicked
 * day) still draws a continuous line.
 */
export function fillDailyGapsInRange<T extends { day: string }>(
  rows: T[],
  start: string,
  end: string,
  emptyFor: (day: string) => T,
): T[] {
  const byDay = new Map(rows.map((r) => [r.day, r]));
  return eachIsoDay(start, end).map((day) => byDay.get(day) ?? emptyFor(day));
}
